import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, EMPTY } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { ChartConfiguration, ChartOptions } from 'chart.js';

import { DashboardWidgetsModule } from 'app/inter-chain/dashboard/widgets/dashboard-widgets.module';
import { OnlineSummaryService } from 'app/inter-chain/online/summary/interchain-online-summary.service';
import { ResponseData, IROTrend } from 'app/inter-chain/online/summary/interchain-online-summary.model';
import { DashboardWidgetCardComponent } from '../dashboard-widget-card/dashboard-widget-card.component';
import {
  buildChartDataset,
  buildPercentChartOptions,
  emptyChartData,
  fillTimeSeriesGaps,
  DECREASE_COLOR,
  DECREASE_BG_COLOR,
  INCREASE_COLOR,
  INCREASE_BG_COLOR,
} from '../../utils/chart-config.util';
import { getIROAndSemanticQualificationKey } from '../../utils/qualifications.util';

@Component({
  selector: 'mh-iro-widget-container',
  templateUrl: './iro-widget-container.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DashboardWidgetsModule, DashboardWidgetCardComponent, TranslateModule],
})
export class IroWidgetContainerComponent {
  customerId = input.required<number>();
  enabled = input(true);

  rawData = signal<ResponseData | null>(null);
  displayValue = signal<string | null>(null);
  loading = signal(true);
  error = signal(false);
  ready = computed(() => !this.loading());
  chartData = signal<ChartConfiguration['data']>(emptyChartData());
  chartOptions = signal<ChartOptions>({});

  changePercentage = computed(() => this.rawData()?.average?.details?.variation?.iro ?? null);
  isIncrease = computed(() => (this.rawData()?.average?.details?.variation?.iro ?? 0) > 0);
  gaKey = computed(() => (this.enabled() ? 'corporatedash-viewon-active' : 'corporatedash-viewon-inactive'));

  private destroyRef = inject(DestroyRef);
  private onlineSummaryService = inject(OnlineSummaryService);
  private translateService = inject(TranslateService);

  constructor() {
    combineLatest([toObservable(this.customerId), toObservable(this.enabled)])
      .pipe(
        switchMap(([, enabled]) => {
          this.reset();
          if (!enabled) {
            this.setDisabled();
            return EMPTY;
          }
          return this.fetchData();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private reset(): void {
    this.rawData.set(null);
    this.displayValue.set(null);
    this.loading.set(true);
    this.error.set(false);
    this.chartData.set(emptyChartData());
    this.chartOptions.set({});
  }

  private setDisabled(): void {
    this.loading.set(false);
  }

  private fetchData() {
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    const monthAgo = DateTime.now().minus({ days: 30 }).toFormat('yyyy-MM-dd');

    return this.onlineSummaryService.getInterChainIROMetricByCustomerIds(`${this.customerId()}`, monthAgo, today).pipe(
      tap((response: ResponseData) => {
        if (!response) return;
        const iroActual = response.average?.details?.actual?.iro;
        response.description = this.translateService.instant(getIROAndSemanticQualificationKey(iroActual));
        this.rawData.set(response);
        this.displayValue.set(iroActual != null ? `${iroActual.toFixed()}%` : null);
      }),
      switchMap((response: ResponseData) => {
        if (!response) return EMPTY;
        return this.onlineSummaryService
          .getInterChainIROTrendByCustomerIds(`${this.customerId()}`, monthAgo, today, 'days')
          .pipe(
            tap((trendsResponse: IROTrend) => {
              const rawPeriods = trendsResponse.average.periods.map((p) => ({ period: p.period, value: p.iro ?? null }));
              const filled = fillTimeSeriesGaps(rawPeriods);
              const iroValues = filled.map((p) => p.value as number);
              const nonNullValues = filled.map((p) => p.value).filter((v): v is number => v !== null);
              const increase = this.isIncrease();
              this.chartOptions.set(buildPercentChartOptions(nonNullValues.length ? Math.max(...nonNullValues) : 100));
              this.chartData.set(
                buildChartDataset(
                  filled.map((p) => p.period),
                  iroValues,
                  increase ? INCREASE_COLOR : DECREASE_COLOR,
                  increase ? INCREASE_BG_COLOR : DECREASE_BG_COLOR,
                ),
              );
            }),
            catchError(() => EMPTY),
          );
      }),
      catchError(() => {
        this.error.set(true);
        return EMPTY;
      }),
      finalize(() => this.loading.set(false)),
    );
  }
}
